export type TEventCB = (...args: any[]) => void;
export type noop = () => void;

/**
 * Test on https://jsbench.me/ .Much faster than Array.prototype.splice
 */
function spliceOne(list: unknown[], index: number) {
    for (let i = index, k = i + 1, n = list.length; k < n; i += 1, k += 1) {
        list[i] = list[k];
    }
    list.pop();
}

/**
 * The EventEmitter is a managing class to manage events subscribe and unsubscribe.
 */
export default class EventEmitter<T extends string = string> {
    private _eventListeners: Record<string, TEventCB[]> = {};

    /**
     * Adds the listener function to the end of the listeners array for the event named eventName.
     * @param eventName
     * @param listener
     * @returns off function handler
     */
    on(eventName: T, listener: TEventCB): noop {
        if (!Array.isArray(this._eventListeners[eventName])) {
            this._eventListeners[eventName] = [];
        }
        this._eventListeners[eventName].push(listener);
        return () => this.off(eventName, listener);
    }

    /**
     * Removes the specified listener from the listener array for the event named eventName.
     * @param eventName
     * @param listener
     */
    off(eventName: T, listener: TEventCB) {
        const listeners = this._eventListeners[eventName];
        if (Array.isArray(listeners)) {
            const removeIndex = listeners.indexOf(listener);
            if (removeIndex > -1) {
                spliceOne(listeners, removeIndex);
            }
        }
    }

    /**
     * Adds a one-time listener function for the event named eventName. The next time eventName is triggered, this listener is removed and then invoked.
     * @param eventName
     * @param listener
     */
    once(eventName: T, listener: TEventCB) {
        const tmpListener = (...args:any[]) => {
            this.off(eventName, tmpListener);
            listener.apply(this, args);
        };
        this.on(eventName, tmpListener);
    }

    /**
     * @internal
     */
    emit(eventName: T, ...args: unknown[]) {
        if (Array.isArray(this._eventListeners[eventName])) {
            this._eventListeners[eventName].forEach(listener => listener.apply(this, args));
        }
    }

    /**
     * @internal
     */
    removeAllListeners() {
        this._eventListeners = {};
    }
}
